// WCAG helpers shared by every page that paints a user-defined color (Data Center
// tag/source colors) behind text. The app's accessibility target is a contrast
// ratio of at least MIN_CONTRAST (6:1) for all text, in light and dark mode.

export const MIN_CONTRAST = 6;
// A little headroom so rounding / anti-aliasing never pushes a pair under 6.
const TARGET = 6.3;

const DARK_TEXT = "#111827";
const LIGHT_TEXT = "#ffffff";

export const normalizeHex = (c) => {
  const s = String(c || "").trim();
  if (!s) return "";
  return s.startsWith("#") ? s : `#${s}`;
};

const toRgb = (hex) => {
  const h = normalizeHex(hex).replace("#", "");
  const full = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  if (full.length !== 6) return null;
  const n = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return n.some(Number.isNaN) ? null : { r: n[0], g: n[1], b: n[2] };
};

const toHex = ({ r, g, b }) =>
  "#" +
  [r, g, b]
    .map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0"))
    .join("");

const luminance = ({ r, g, b }) => {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

export const contrastRatio = (hexA, hexB) => {
  const a = toRgb(hexA);
  const b = toRgb(hexB);
  if (!a || !b) return 1;
  const l1 = luminance(a);
  const l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

// Black or white text, whichever reads better on `bg`.
export const bestTextOn = (bg) =>
  contrastRatio(bg, DARK_TEXT) >= contrastRatio(bg, LIGHT_TEXT) ? DARK_TEXT : LIGHT_TEXT;

const rgbToHsl = ({ r, g, b }) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h / 6, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  if (s === 0) { const v = l * 255; return { r: v, g: v, b: v }; }
  const hue = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return { r: hue(p, q, h + 1 / 3) * 255, g: hue(p, q, h) * 255, b: hue(p, q, h - 1 / 3) * 255 };
};

// Returns `hex` unchanged when black or white text already reaches the target on
// it; otherwise nudges its lightness just far enough (keeping hue/saturation, so
// it still reads as the same color) that the better of the two text colors does.
// Use it for any background whose color is chosen by a user.
export const accessibleBg = (hex) => {
  const rgb = toRgb(hex);
  if (!rgb) return hex;
  const best = (h) => Math.max(contrastRatio(h, DARK_TEXT), contrastRatio(h, LIGHT_TEXT));
  if (best(hex) >= TARGET) return normalizeHex(hex);

  const hsl = rgbToHsl(rgb);
  // The mid-tones that fail are closer to one side: go the shorter way.
  const goDarker = contrastRatio(hex, LIGHT_TEXT) >= contrastRatio(hex, DARK_TEXT);
  let candidate = normalizeHex(hex);
  for (let i = 0; i < 60; i += 1) {
    hsl.l = Math.max(0, Math.min(1, hsl.l + (goDarker ? -0.01 : 0.01)));
    candidate = toHex(hslToRgb(hsl));
    if (best(candidate) >= TARGET) return candidate;
  }
  return candidate;
};
